import React, { useContext, useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useLazyQuery, useMutation } from '@apollo/client';
import { motion } from 'framer-motion';
import { useAppDispatch } from 'src/hooks';
import Storage from 'src/utils/storage/storage';
import { useNavigate } from 'react-router-dom';
import { setNavigate } from "../../../utils/navigateHelper";
import {
  getUserMetadata,
  updateUserMetadata
} from 'src/components/user/user/store/user-slice';
import useNotificationWebSocket from 'src/pages/core/websocket/notification-websocket.hook';
import UserNavbar from './UserNavbar';
import UserSidebar from './UserSidebar';
import { CustomThemeContext } from '../../../theme/CustomThemeProvider';
import { USER_QUERY, UPDATE_SIDEBAR_MUTATION } from '../../../graph/UserGraph';
import { SITE_INFORMATION_QUERY } from '../../../graph/SiteGraph';
import ProgressStatus from '../../global/ProgressStatus';
import { setIsDrawerOpen } from '../appDrawer/app-drawer-slice';

const drawerWidth = 280;

const UserLayoutRoot = styled(motion.div)(({ theme, open }) => ({
  backgroundColor: theme.palette.primary.background,
  display: 'flex',
  height: '100%',
  width: '100%',
  overflow: 'hidden'
}));

const UserLayoutWrapper = styled('div')(({ theme, open }) => ({
  display: 'flex',
  flex: '1 1 auto',
  overflow: 'hidden',
  paddingTop: '42px',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open && {
    // marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  })
}));

const UserLayoutContainer = styled('div')(({ theme, open }) => ({
  display: 'flex',
  flex: '1 1 auto',
  overflow: 'hidden'
  // paddingLeft: theme.spacing(7),
}));

const UserLayoutContent = styled('div')(({ theme, open }) => ({
  flex: '1 1 auto',
  height: '100%',
  overflow: 'auto',
  backgroundColor: '#f9f9f9'
  // padding: theme.spacing(3)
}));

function UserLayout() {
  const { currentTheme, setTheme } = useContext(CustomThemeContext);
  const appDispatch = useAppDispatch();
  const [userData, setUserData] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userTheme, setUserTheme] = useState(null);
  const [isDark, setIsDark] = useState(null);
  const [sidebar, setSidebar] = useState(null);

  const [getLazyUser, lazyUser] = useLazyQuery(USER_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (userData) => {
      localStorage.setItem('userID', userData.me.id);
    }
  });
  useNotificationWebSocket();
  const [getLazySiteInfo, lazySiteInfo] = useLazyQuery(SITE_INFORMATION_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only', // Used for first execution
    nextFetchPolicy: 'cache-first', // Used for subsequent executions
    variables: {
      // siteName: window.location.host.split('.')[0],
      siteName: 'srm-ppa'
    }
  });

  const [updateSidebar] = useMutation(UPDATE_SIDEBAR_MUTATION);
  const navigate = useNavigate();
  setNavigate(navigate); // Set navigate function globally

  useEffect(() => {
    // checkAuthenticated()
    getLazyUser({
      onCompleted: (userData) => {
        localStorage.setItem('userID', userData.me.id);
        setUserData(userData);

        if (userData?.preferences !== null) {
          console.log('user preference exists✅');
          setIsSidebarOpen(userData.preferences.sidebar);
          setUserTheme(userData.preferences.theme.name);
          setIsDark(Boolean(userTheme === 'dark'));
        } else {
          console.log('went into else statement⌛');
          lazyUser.refetch();
        }
      }
    });

    getLazySiteInfo({
      onCompleted: (info) => {
        // console.log(info.siteInfo[0], "this is lazy site info")
        const siteInfoData = info.siteInfo[0];
        localStorage.setItem('site_info', JSON.stringify(siteInfoData));
        localStorage.setItem('site_title', siteInfoData.site.title);
        localStorage.setItem('site_name', siteInfoData.site.name);
      }
    });

    /**
     * User metadata and screen metadata must always be set in the loader function of this components route config
     */
    appDispatch(updateUserMetadata());

    /**
     * Loading metadata for screens and user
     */
    if (!Storage.getUserMetadata().userMeta) {
      appDispatch(getUserMetadata());
    }
  }, []);

  const handleDrawerOpen = () => {
    console.log('open got clicked');
    updateSidebar({
      variables: {
        sidebar: true,
        id: userData.preferences.id,
        userId: userData.me.id
      }
    });
    setIsSidebarOpen(true);
    localStorage.setItem('sidebarStatus', 'open');
    // setting drawer to open
    appDispatch(setIsDrawerOpen(!isSidebarOpen));
  };

  const handleDrawerClose = (event, reason) => {
    console.log('close got clicked');
    updateSidebar({
      variables: {
        sidebar: false,
        id: userData.preferences.id,
        userId: userData.me.id
      }
    });
    setIsSidebarOpen(false);
    localStorage.setItem('sidebarStatus', 'close');
    // setting drawer to close
    appDispatch(setIsDrawerOpen(!isSidebarOpen));
    if (
      reason === 'backdropClick' ||
      reason === 'escapeKeyDown' ||
      isSidebarOpen === false
    ) {
    }
  };

  return (
    <UserLayoutRoot>
      <UserNavbar
        isDark={isDark}
        theme={userTheme}
        setTheme={setTheme}
        userData={userData}
        drawerWidth={drawerWidth}
        sidebar={sidebar}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        handleDrawerOpen={handleDrawerOpen}
        handleDrawerClose={handleDrawerClose}
      />
      <UserSidebar
        isDark={isDark}
        theme={userTheme}
        userData={userData}
        drawerWidth={drawerWidth}
        setSidebar={setSidebar}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        handleDrawerOpen={handleDrawerOpen}
        handleDrawerClose={handleDrawerClose}
      />
      <UserLayoutWrapper open={isSidebarOpen}>
        <UserLayoutContainer>
          <ProgressStatus />
          <UserLayoutContent>
            {/* <Suggestions /> */}
            <Outlet />
          </UserLayoutContent>
        </UserLayoutContainer>
      </UserLayoutWrapper>
    </UserLayoutRoot>
  );
}

export default UserLayout;
